<template>
  <div class="build-type-content">
    <div class="build-type-details-container">
      <div class="build-type-details-container-header">
        {{ titleText }}
        <AuditInformation 
          v-if="!loading && buildType && buildType.id > 0 && buildType.createdByUser && buildType.createdDate && buildType.lastModifiedByUser && buildType.lastModifiedDate"
          :created-by-user="buildType.createdByUser"
          :created-date="buildType.createdDate"
          :last-modified-by-user="buildType.lastModifiedByUser"
          :last-modified-date="buildType.lastModifiedDate"
        />
      </div>
      <LoadingPulse
        :loading="loading"
        class="loading-spinner"
      />
      <div
        v-if="!loading && buildType"
        class="build-type-details-container-content"
      >
        <LockingButton
          class="save-button"
          :disabled="!isDirty"
          :locked="saving"
          :text="'Save'"
          @click="onSaveBuildTypeClicked"
        />
        <div class="grouped-inputs">
          <TextInput
            v-model="buildType.name"
            :show-required="true"
            label="Name"
            class="first"
          />
          <div class="checkbox-input-group">
            <Checkbox
              v-model="buildType.cartridgeRequired"
              label="Cartridge Required?"
              class="second-checkbox"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue'
import TextInput from '@/components/shared/TextInput.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import { BuildTypeDetails } from '@/@types/responses/build_type/BuildTypeDetails'
import AuditInformation from '@/components/elements/AuditInformation.vue'
import { BuildTypeService } from '@/services/buildType.service'
import Checkbox from '@/components/shared/Checkbox.vue'
import { BuildTypeMessage } from '@/@types/messages/BuildTypeMessage'
import LockingButton from '@/components/shared/LockingButton.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'InventoryItem',
  components: {
    AuditInformation,
    Checkbox,
    LoadingPulse,
    LockingButton,
    TextInput
  },
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  setup() {
    const buildTypeService = inject(PROVIDER_CONSTANTS.buildTypeService) as BuildTypeService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      buildTypeService,
      toast
    }
  },
  data() {
    let buildType: BuildTypeDetails|undefined
    let unchangedBuildType: BuildTypeDetails|undefined

    return {
      buildType,
      loading: true,
      saving: false,
      unchangedBuildType
    }
  },
  computed: {
    isDirty(): boolean {
      const a = this.buildType
      const b = this.unchangedBuildType

      return (a?.name !== b?.name || a?.cartridgeRequired !== b?.cartridgeRequired) && a?.name.trim() !== ''
    },
    titleText(): string {
      return !this.buildType || this.buildType.id === 0 ? 'Create Build Type' : 'Edit Build Type'
    }
  },
  watch: {
    '$route': {
      handler: function(): void {
        this.updatePageTitle()
      }
    }
  },
  mounted() {
    if (this.id > 0) {
      this.buildTypeService.getBuildType(this.id).then(buildType => {
        this.buildType = buildType
        this.unchangedBuildType = JSON.parse(JSON.stringify(buildType))
        this.updatePageTitle()
        this.loading = false
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the build type. Try reloading the page.')
        }
      })
    } else {
      this.buildType = new BuildTypeDetails(0)
      this.unchangedBuildType = new BuildTypeDetails(0)
      this.loading = false
    }
  },
  methods: {
    onSaveBuildTypeClicked(): void {
      if (!this.buildType) return

      this.saving = true

      const buildTypeMessage = new BuildTypeMessage(this.buildType.cartridgeRequired, this.buildType.name)
      if (this.buildType.id === 0)
        this.buildTypeService.createBuildType(buildTypeMessage).then(objectCreatedResponse => {
          this.$router.replace(`/build-type/${objectCreatedResponse.id}`)
          this.saving = false
          this.toast.success('The build type was created successfully.')
          this.$router.push('/build-types')
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to create the build type. Please try again.')
          }
        })
      else
        this.buildTypeService.updateBuildType(this.id, buildTypeMessage).then(() => {
          this.toast.success('The build type was updated successfully.')
          this.saving = false
          this.$router.push('/build-types')
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to update the build type. Please try again.')
          }
        })
    },
    updatePageTitle(): void {
      if (this.id > 0 && this.buildType)
        document.title = `${this.buildType.name} - Short Action Customs, LLC`
    }
  }
})
</script>

<style scoped lang="scss">
.build-type-content {
  padding: 15px;
  background-color: $Color-fill-gray;
  min-height: calc(100vh - 94px);

  @include desktop {
    min-height: calc(100vh - 130px);
  }

  > .build-type-details-container {
    padding-bottom: 15px;
    position: relative;

    > .build-type-details-container-header {
      line-height: 37px;
      font-size: 24px;
      font-weight: bold;
      padding-bottom: 10px;
      border-bottom: 1px solid $Color-border-gray;
    }

    > .loading-spinner {
      position: absolute;
      top: 50%;
      right: 50%;
      padding: 20px;
      transform: translate(50%, 50%);
    }

    > .build-type-details-container-content {
      padding-top: 15px;

      > .save-button {
        position: absolute;
        top: 0;
        right: 0;
      }

      > .grouped-inputs {
        display: grid;
        grid-gap: 15px;
        grid-template-columns: 1fr;

        @include tablet {
          grid-template-columns: 1fr 1fr;
        }

        > .checkbox-input-group {
          display: flex;
          flex-direction: column;
          justify-content: flex-end;

          > .second-checkbox {
            margin-bottom: 2px;
          } 
        }
      }
    }
  }
}
</style>
